import { Flex, Text } from '@radix-ui/themes';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import OnomondoLogo from '~/components/OnomondoLogo';

const StyledGiantText = styled(Text)`
  font-size: 120px;
`;

const Stack = styled.pre`
  overflow: auto;
  font-size: 0.7em;
  background-color: var(--neutral-a4);
  padding: 5px;
`;

class ErrorBoundary extends React.Component {
  state = {
    hasError: false,
    hasChunkError: false,
    error: null,
  };

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    const isChunkLoadError = error.name === 'ChunkLoadError';
    return {
      hasChunkError: isChunkLoadError,
      hasError: !isChunkLoadError,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    const currentUrl = window.location.href;
    if (currentUrl.includes('fake-error-page')) return;

    if (this.state.hasChunkError) {
      window.location.reload();
      return;
    }
    Sentry.withScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key]);
      });
      Sentry.captureException(error);
    });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const isProduction = process.env.NODE_ENV === 'production';

    return (
      <Flex
        direction='column'
        align='center'
        gap='4'
        pt='80px'
      >
        <Flex
          direction='column'
          align='center'
          gap='2'
        >
          <Flex
            align='center'
            gap='1'
          >
            <StyledGiantText weight='bold'>ERR</StyledGiantText>
            <OnomondoLogo
              size={120}
              animated
              primaryColor='gray'
            />
            <StyledGiantText weight='bold'>R</StyledGiantText>
          </Flex>
          <Text
            size='6'
            weight='medium'
          >
            Something went wrong
          </Text>
          <Flex
            align='center'
            direction='column'
            gap='2'
          >
            <Flex
              direction='column'
              align='center'
            >
              <Text
                size='2'
                color='gray'
              >
                This was the message:
              </Text>
              <Text
                size='2'
                color='gray'
              >
                {this.state.error.message}
              </Text>
            </Flex>
            <Text size='2'>The team has been notified.</Text>
            <a href={window.location.href}>
              <Text
                size='2'
                color='gray'
                weight='medium'
              >
                Click here to try again, or reload the page
              </Text>
            </a>
          </Flex>
        </Flex>
        {!isProduction && (
          <div>
            <Stack>{this.state.error.stack}</Stack>
          </div>
        )}
      </Flex>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
};

export default ErrorBoundary;
